import { UzFormField, UzInput } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';

import type { CreateWebsiteCategoryPageRequest } from '@utazzo/shared';

type SeoLink = CreateWebsiteCategoryPageRequest['seoLinks'][number];

interface LinkRef {
  index: number;
}
interface Group {
  label: string;
  /** Flat index of the group's first link — used as a stable-ish React key. */
  start: number;
  items: LinkRef[];
}

/**
 * SEO links editor grouped by their shared heading: each group shows ONE
 * "Group label" with its links (label + URL) nested beneath, plus "Add link"
 * per group and "Add group". The form still stores a flat
 * `{ groupLabel, label, href }[]` (the public page clusters by groupLabel), so
 * group edits write the same label onto every link in the group.
 */
export const SeoLinksEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteCategoryPageRequest>;
  disabled?: boolean;
}) => {
  const { fields, insert, remove, append } = useFieldArray({
    control: form.control,
    name: 'seoLinks',
  });
  const links = (useWatch({ control: form.control, name: 'seoLinks' }) ?? []) as SeoLink[];

  // Fold the flat list into contiguous runs that share a group label.
  const groups: Group[] = [];
  links.forEach((link, index) => {
    const label = link.groupLabel ?? '';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push({ index });
    else groups.push({ label, start: index, items: [{ index }] });
  });

  const setGroupLabel = (group: Group, value: string): void => {
    for (const { index } of group.items) {
      form.setValue(`seoLinks.${index}.groupLabel` as const, value, { shouldDirty: true });
    }
  };

  const addLinkToGroup = (group: Group): void => {
    const lastIndex = group.items[group.items.length - 1]!.index;
    insert(lastIndex + 1, { groupLabel: group.label, label: '', href: '' });
  };

  const removeGroup = (group: Group): void => {
    remove(group.items.map((it) => it.index));
  };

  const addGroup = (): void => {
    // A distinct default label keeps a fresh group from merging into an
    // adjacent empty one (groups are keyed by their label).
    const used = new Set(groups.map((g) => g.label));
    let n = groups.length + 1;
    let label = `New group ${n}`;
    while (used.has(label)) label = `New group ${++n}`;
    append({ groupLabel: label, label: '', href: '' });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500">
        Internal-link clusters shown at the bottom of the public category page. Each group shows one
        heading with its links beneath it.
      </p>

      {groups.map((group) => (
        <div
          key={fields[group.start]?.id ?? group.start}
          className="rounded-md border border-zinc-200 bg-zinc-50 p-3"
        >
          <div className="mb-3 flex items-end gap-2">
            <div className="flex-1">
              <UzInput
                value={group.label}
                onChange={(e) => setGroupLabel(group, e.target.value)}
                label="Group label"
                placeholder="Explore Packages by Theme"
                disabled={disabled}
              />
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeGroup(group)}
                className="mb-2.5 text-zinc-400 hover:text-rose-600"
                aria-label="Remove group"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="space-y-2">
            {group.items.map(({ index }) => (
              <div
                key={fields[index]?.id ?? index}
                className="flex items-end gap-2 rounded-md border border-zinc-200 bg-white p-2"
              >
                <div className="flex-1">
                  <UzFormField name={`seoLinks.${index}.label` as const}>
                    {({ field, error }) => (
                      <UzInput
                        {...field}
                        value={(field.value as string | null) ?? ''}
                        label="Link label"
                        error={error}
                        disabled={disabled}
                      />
                    )}
                  </UzFormField>
                </div>
                <div className="flex-1">
                  <UzFormField name={`seoLinks.${index}.href` as const}>
                    {({ field, error }) => (
                      <UzInput
                        {...field}
                        value={(field.value as string | null) ?? ''}
                        label="Link URL"
                        placeholder="/india-tour-packages"
                        error={error}
                        disabled={disabled}
                      />
                    )}
                  </UzFormField>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="mb-2.5 text-zinc-400 hover:text-rose-600"
                    aria-label="Remove link"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!disabled && (
              <button
                type="button"
                onClick={() => addLinkToGroup(group)}
                className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-zinc-300 py-1.5 text-xs font-medium text-zinc-500 hover:border-zinc-400 hover:text-zinc-700"
              >
                <Plus className="h-3.5 w-3.5" /> Add link
              </button>
            )}
          </div>
        </div>
      ))}

      {!disabled && (
        <button
          type="button"
          onClick={addGroup}
          className="w-full rounded-md border border-dashed border-zinc-300 py-2 text-xs font-medium text-zinc-500 hover:border-zinc-400 hover:text-zinc-700"
        >
          + Add group
        </button>
      )}
    </div>
  );
};
